/**
 * Level 1 of the hedge hierarchy: account pairs.
 *
 * Each row here represents a two-account hedge (one long, one short).
 * Drilling in goes to /pairs/:slug which lists the symbol hedges
 * underneath it (level 2).
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2 } from "@/components/ui/icons";
import { DashboardSection } from "@/components/dashboard-section";
import { AccountPairCard } from "@/components/account-pair-card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/page-header";
import { CoolIcon } from "@/components/ui/cool-icon";
import {
  listAccountPairs,
  rematchAllAccountPairs,
  rebuildImperfectAccountPairs,
  reopenAccountPair,
  type AccountPairListItem,
} from "@/lib/api/account-pairs";
import { listPairProposals } from "@/lib/api/pair-proposals";
import { listAccounts, type AccountRow } from "@/lib/api/accounts";
import { ManualPairDialog } from "@/components/manual-pair-dialog";
import { useConfirm } from "@/components/ui/dialog-imperative";
import { cn } from "@/lib/utils";

const FILTERS = [
  { key: "all", label: "All" },
  { key: "active", label: "Active" },
  { key: "paused", label: "Paused" },
  { key: "archived", label: "Archived" },
] as const;

type FilterKey = (typeof FILTERS)[number]["key"];

export function AccountPairsPage(): ReactNode {
  const navigate = useNavigate();
  const confirm = useConfirm();
  // Default-fetched list (status != closed) — covers active/paused/all tabs.
  const [accountPairs, setAccountPairs] = useState<AccountPairListItem[] | null>(
    null,
  );
  // Archived list is fetched lazily when the tab is selected; kept in
  // separate state so switching tabs doesn't trigger a refetch.
  const [archivedPairs, setArchivedPairs] = useState<
    AccountPairListItem[] | null
  >(null);
  const [archivedLoading, setArchivedLoading] = useState(false);
  const [filter, setFilter] = useState<FilterKey>("all");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [proposalsCount, setProposalsCount] = useState<number>(0);
  const [rematchingAll, setRematchingAll] = useState(false);
  const [rebuildingImperfect, setRebuildingImperfect] = useState(false);
  const [manualPairOpen, setManualPairOpen] = useState(false);
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [reopeningSlug, setReopeningSlug] = useState<string | null>(null);

  const reloadAll = useCallback((): void => {
    setLoadError(null);
    setAccountPairs(null);
    listAccountPairs()
      .then(setAccountPairs)
      .catch((e) => {
        setAccountPairs([]);
        setLoadError(e instanceof Error ? e.message : String(e));
      });
    // Proposals are a secondary fetch — render the list even if the
    // proposals call fails, so a backend issue on one endpoint doesn't
    // black out the whole page.
    listPairProposals()
      .then((list) => setProposalsCount(list.length))
      .catch(() => setProposalsCount(0));
    // Account roster powers the manual pair builder dialog.
    listAccounts()
      .then(setAccounts)
      .catch(() => setAccounts([]));
  }, []);

  useEffect(() => {
    reloadAll();
  }, [reloadAll]);

  useEffect(() => {
    if (filter !== "archived" || archivedPairs !== null) return;
    setArchivedLoading(true);
    listAccountPairs("archived")
      .then(setArchivedPairs)
      .catch((e) => {
        setArchivedPairs([]);
        toast.error(
          `Couldn’t load archived pairs: ${e instanceof Error ? e.message : String(e)}`,
        );
      })
      .finally(() => setArchivedLoading(false));
  }, [filter, archivedPairs]);

  const safe = accountPairs ?? [];
  const filtered = useMemo(() => {
    if (filter === "archived") return archivedPairs ?? [];
    if (filter === "all") return safe;
    return safe.filter((p) => p.status === filter);
  }, [filter, safe, archivedPairs]);

  const filterCounts = useMemo(() => {
    const c: Record<string, number> = { all: safe.length };
    for (const p of safe) c[p.status] = (c[p.status] ?? 0) + 1;
    if (archivedPairs !== null) c.archived = archivedPairs.length;
    return c;
  }, [safe, archivedPairs]);

  const handleRebuildImperfect = async (): Promise<void> => {
    const ok = await confirm({
      title: "Rebuild Every Non-Perfect Pair?",
      description:
        "This dissolves near/mismatch pairs and re-matches both legs against the current pool. Existing perfect pairs are left alone.",
      confirmText: "Rebuild All",
    });
    if (!ok) return;
    setRebuildingImperfect(true);
    try {
      const tally = await rebuildImperfectAccountPairs();
      const parts: string[] = [];
      if (tally.dissolved > 0) parts.push(`${tally.dissolved} dissolved`);
      if (tally.paired > 0) parts.push(`${tally.paired} re-paired`);
      if (tally.proposalsCreated > 0) parts.push(`${tally.proposalsCreated} proposals`);
      if (tally.stillUnpaired > 0) parts.push(`${tally.stillUnpaired} still unpaired`);
      if (tally.errors > 0) parts.push(`${tally.errors} errors`);
      toast.success(`Rebuild: ${parts.length > 0 ? parts.join(", ") : "no imperfect pairs"}`);
      const [fresh, proposals] = await Promise.all([
        listAccountPairs(),
        listPairProposals().catch(() => []),
      ]);
      setAccountPairs(fresh);
      setProposalsCount(proposals.length);
    } catch (e) {
      toast.error(`Rebuild failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setRebuildingImperfect(false);
    }
  };

  const handleRematchAll = async (): Promise<void> => {
    setRematchingAll(true);
    try {
      const tally = await rematchAllAccountPairs();
      const parts: string[] = [];
      if (tally.paired > 0) parts.push(`${tally.paired} paired`);
      if (tally.proposalsCreated > 0)
        parts.push(`${tally.proposalsCreated} proposals`);
      if (tally.alreadyPaired > 0)
        parts.push(`${tally.alreadyPaired} already paired`);
      if (tally.errors > 0) parts.push(`${tally.errors} errors`);
      const summary = parts.length > 0 ? parts.join(", ") : "no changes";
      toast.success(`Rematch: scanned ${tally.scanned} — ${summary}`);
      if (tally.paired > 0 || tally.proposalsCreated > 0) {
        // Refetch so the new pairs appear; proposals count is updated
        // too in case the user wants to drill into the approvals queue.
        const [fresh, proposals] = await Promise.all([
          listAccountPairs(),
          listPairProposals().catch(() => []),
        ]);
        setAccountPairs(fresh);
        setProposalsCount(proposals.length);
      }
    } catch (e) {
      toast.error(
        `Rematch failed: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setRematchingAll(false);
    }
  };

  const handleReopen = async (slug: string): Promise<void> => {
    setReopeningSlug(slug);
    try {
      await reopenAccountPair(slug);
      navigate(`/pairs/${encodeURIComponent(slug)}`);
    } catch (e) {
      toast.error(
        `Couldn’t reopen: ${e instanceof Error ? e.message : String(e)}`,
      );
    } finally {
      setReopeningSlug(null);
    }
  };

  return (
    <div className="flex flex-col gap-5 font-sans">
      <PageHeader
        title="Account Pairs"
        subtitle="Two Accounts Paired As A Hedge. New Pairs Start Paused So You Can Finish Setup Before Trading."
        icon={<CoolIcon name="Arrow_Left_Right" />}
        accentWords={["Pairs"]}
        right={
          <div className="grid w-full grid-cols-2 items-center gap-2 sm:flex sm:w-auto sm:flex-wrap">
            <Button
              size="sm"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => void handleRematchAll()}
              disabled={rematchingAll || rebuildingImperfect}
              aria-busy={rematchingAll}
            >
              {rematchingAll && (
                <Loader2 aria-hidden="true" className="mr-1 h-3 w-3 animate-spin" />
              )}
              {rematchingAll ? "Matching…" : "Rematch All"}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => void handleRebuildImperfect()}
              disabled={rematchingAll || rebuildingImperfect}
              aria-busy={rebuildingImperfect}
            >
              {rebuildingImperfect && (
                <Loader2 aria-hidden="true" className="mr-1 h-3 w-3 animate-spin" />
              )}
              {rebuildingImperfect ? "Rebuilding…" : "Rebuild Imperfect"}
            </Button>
            <Button
              size="sm"
              className="w-full sm:w-auto"
              onClick={() => setManualPairOpen(true)}
              disabled={rematchingAll || rebuildingImperfect}
            >
              Pair Manually
            </Button>
            <Link
              to="/accounts"
              className="inline-flex w-full items-center justify-center rounded-lg bg-accent px-3.5 py-2 text-xs font-medium text-white transition-colors hover:bg-accent/90 sm:w-auto"
            >
              Manage Accounts
            </Link>
          </div>
        }
      />

      {/* Proposals banner — shown only when the auto-matcher has queued
          imperfect pairings for operator review. Dropped silently when
          the queue is empty so the page stays clean on a normal day. */}
      {proposalsCount > 0 && (
        <Link
          to="/pair-approvals"
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-amber-500/40 bg-amber-500/5 px-4 py-3 text-sm text-amber-500 transition-colors hover:bg-amber-500/10"
        >
          <span className="font-medium">
            {proposalsCount} Pair{" "}
            {proposalsCount === 1 ? "Proposal Needs" : "Proposals Need"} Your
            Approval
          </span>
          <span className="text-xs underline">Review</span>
        </Link>
      )}

      {/* Filter tabs */}
      <div
        role="tablist"
        aria-label="Filter account pairs"
        className="-mx-3 flex items-center gap-1 overflow-x-auto px-3 pb-1 [scrollbar-width:none] sm:mx-0 sm:px-0 [&::-webkit-scrollbar]:hidden"
      >
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            role="tab"
            aria-selected={filter === f.key}
            onClick={() => setFilter(f.key)}
            style={{ touchAction: "manipulation" }}
            className={cn(
              "shrink-0 whitespace-nowrap rounded-lg px-3 py-1.5 text-xs font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/60",
              filter === f.key
                ? "bg-accent/10 text-accent"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            {f.label}
            <span className="ml-1.5 font-mono text-[10px] tabular-nums opacity-60">
              {filterCounts[f.key] ?? 0}
            </span>
          </button>
        ))}
      </div>

      {/* Grid */}
      {accountPairs === null || (filter === "archived" && archivedLoading) ? (
        <div
          aria-busy="true"
          aria-label={filter === "archived" ? "Loading archived pairs" : "Loading account pairs"}
          className="grid gap-4 auto-rows-fr md:grid-cols-2 2xl:grid-cols-3"
        >
          {[0, 1, 2, 3].map((i) => (
            <div
              key={i}
              className="flex h-full flex-col gap-4 rounded-xl border border-border bg-card/50 p-4 sm:p-5"
            >
              <div className="flex items-center justify-between">
                <Skeleton className="h-4 w-24" />
                <Skeleton className="h-4 w-16" />
              </div>
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-lg" />
                <div className="flex flex-1 flex-col gap-1.5">
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="h-3 w-20" />
                </div>
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex flex-1 flex-col items-end gap-1.5">
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="h-3 w-20" />
                </div>
              </div>
              <div className="mt-auto grid grid-cols-3 gap-3 border-t border-border pt-3">
                <div className="flex flex-col gap-1.5">
                  <Skeleton className="h-2.5 w-12" />
                  <Skeleton className="h-3 w-8" />
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <Skeleton className="h-2.5 w-16" />
                  <Skeleton className="h-3 w-14" />
                </div>
                <div className="flex flex-col items-end gap-1.5">
                  <Skeleton className="h-2.5 w-16" />
                  <Skeleton className="h-3 w-14" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : loadError ? (
        <DashboardSection>
          <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
            <p role="alert" aria-live="polite" className="text-sm text-destructive">
              Couldn’t load account pairs: {loadError}
            </p>
            <Button size="sm" variant="outline" onClick={reloadAll}>
              Retry
            </Button>
          </div>
        </DashboardSection>
      ) : filtered.length === 0 ? (
        <DashboardSection>
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="mb-1 text-sm font-medium text-foreground">
              {filter === "archived" ? "No Archived Pairs" : "No Account Pairs Yet"}
            </p>
            <p className="mb-4 max-w-sm text-xs text-muted-foreground">
              {filter === "archived"
                ? "Archived pairs appear here. Reopen one from its detail page to resume trading."
                : "Pair two accounts (one designated long, one short) to set up the hedge. New pairs start paused, so you can review symbols and allowlist before resuming trading."}
            </p>
            {filter !== "archived" && (
              <Link
                to="/accounts"
                className="rounded-lg bg-accent px-3.5 py-2 text-xs font-medium text-white transition-colors hover:bg-accent/90"
              >
                Manage Accounts
              </Link>
            )}
          </div>
        </DashboardSection>
      ) : (
        <div className="grid gap-4 auto-rows-fr md:grid-cols-2 2xl:grid-cols-3">
          {filtered.map((pair, i) => (
            <div key={pair.id} className="relative flex flex-col">
              <AccountPairCard pair={pair} index={i} />
              {filter === "archived" && (
                <Button
                  size="sm"
                  variant="outline"
                  className="mt-2"
                  onClick={() => void handleReopen(pair.slug)}
                  disabled={reopeningSlug === pair.slug}
                  aria-busy={reopeningSlug === pair.slug}
                >
                  {reopeningSlug === pair.slug && (
                    <Loader2 aria-hidden="true" className="mr-1 h-3 w-3 animate-spin" />
                  )}
                  {reopeningSlug === pair.slug ? "Reopening…" : "Reopen Pair"}
                </Button>
              )}
            </div>
          ))}
        </div>
      )}

      <ManualPairDialog
        open={manualPairOpen}
        onOpenChange={setManualPairOpen}
        accounts={accounts}
        onPaired={async (res) => {
          // Refetch pairs + accounts so the new pair lands in the list
          // and the manual-pair picker's eligible pool reflects the
          // newly-consumed legs.
          const [fresh, freshAccounts] = await Promise.all([
            listAccountPairs(),
            listAccounts().catch(() => accounts),
          ]);
          setAccountPairs(fresh);
          setAccounts(freshAccounts);
          if (res.accountPairSlug) navigate(`/pairs/${encodeURIComponent(res.accountPairSlug)}`);
        }}
      />
    </div>
  );
}
